import { Resources } from '@ragu-components/ord-resources';
import { contactsResource } from '@ragu-components/ord-views';

import { Resource } from 'src/components/resource/Resource';
import { QueueCallSessions, CallRecordings, PersonalCallRecordings, Schedules } from 'src/lib/resources';

import { callflowsResource } from '../dialplan/CallflowsResource';

import {
  assignedCarriersResource,
  assignedInboundE164sResource,
  assignedLcrsResource,
  assignedSipProfilesResource,
  authProvidersResource,
  calendarEventsResource,
  calendarsResource,
  callHistoryResource,
  callRecordingConfsResource,
  callRecordingsResource,
  personalCallRecordingsResource,
  callSessionsResource,
  queueCallSessionsResource,
  extensionResource,
  groupsResource,
  huntingGroupsResource,
  labelsResource,
  carrierMappingsResource,
  userMappingsResource,
  queuesResource,
  sipIdentitiesResource,
  soundfilesResource,
  taskResource,
  userNumbersResource,
  userResource,
  distributionListsResource,
  rolesResource,
  oauthApplicationsResource,
  encryptionConfigurationsResource,
  schedulesResource,
  storageConfigurationsResource,
} from './admin';
import { presenceResource } from './admin/presence';
import { queueMeasurementsResource } from './admin/queue_measurements';
import { serviceClassesResource } from './admin/serviceClasses';
import {
  personalAccessTokensResource,
  personalCalendarsResource,
  personalCalendarEventsResource,
  personalHuntingGroupsResource,
  personalMobilityResource,
  adminMobilityResource,
  personalUserNumbersResource,
} from './personal';
import {
  apSettingsResource,
  apAuthenticationResource,
  apConfigurationsResource,
  apGroupsResource,
  apTemplatesResource,
  domainsResources,
  lcrsResources,
  scimProvisioningsResource,
  sipDevicesResource,
  sipProfilesResources,
  carriersResources,
  inboundE164Resource,
} from './system';

/** https://marmelab.com/react-admin/Resource.html */
export const RESOURCES = {
  // PERSONAL
  [`me/${Resources.mobility_configurations}`]: (
    <Resource {...personalMobilityResource} key={personalMobilityResource.key} />
  ),
  [`me/${Resources.calendars}`]: <Resource {...personalCalendarsResource} key={personalCalendarsResource.key} />,
  [Resources['me/calendar_events']]: (
    <Resource {...personalCalendarEventsResource} key={personalCalendarEventsResource.key} />
  ),
  [Resources['me/personal_access_tokens']]: (
    <Resource {...personalAccessTokensResource} key={personalAccessTokensResource.key} />
  ),
  [`me/${Resources.hunting_groups}`]: (
    <Resource {...personalHuntingGroupsResource} key={personalHuntingGroupsResource.key} />
  ),
  [`me/${Resources.user_numbers}`]: <Resource {...personalUserNumbersResource} key={personalUserNumbersResource.key} />,
  [Resources['me/call_history']]: <Resource {...callHistoryResource} key={callHistoryResource.key} />,
  [PersonalCallRecordings]: <Resource {...personalCallRecordingsResource} key={personalCallRecordingsResource.key} />,

  // ADMIN
  [Resources.labels]: <Resource {...labelsResource} key={labelsResource.key} />,
  [Resources.callflows]: <Resource {...callflowsResource} key={callflowsResource.key} />,
  [Resources.user_mappings]: <Resource {...userMappingsResource} key={userMappingsResource.key} />,
  [Resources.carrier_mappings]: <Resource {...carrierMappingsResource} key={carrierMappingsResource.key} />,
  [Resources.queues]: <Resource {...queuesResource} key={queuesResource.key} />,
  [Resources.queue_measurements]: <Resource {...queueMeasurementsResource} key={queueMeasurementsResource.key} />,
  [QueueCallSessions]: <Resource {...queueCallSessionsResource} key={queueCallSessionsResource.key} />,
  [Resources.call_sessions]: <Resource {...callSessionsResource} key={callSessionsResource.key} />,
  [Resources.call_recording_confs]: <Resource {...callRecordingConfsResource} key={callRecordingConfsResource.key} />,
  [CallRecordings]: <Resource {...callRecordingsResource} key={callRecordingsResource.key} />,
  // ADMIN.USERS;
  [Resources.users]: <Resource {...userResource} key={userResource.key} />,
  [Resources.groups]: <Resource {...groupsResource} key={groupsResource.key} />,
  [Resources.distribution_lists]: <Resource {...distributionListsResource} key={distributionListsResource.key} />,
  [Resources.contacts]: <Resource {...contactsResource} key={contactsResource.key} />,
  [`${Resources.presence}/${Resources.users}`]: <Resource {...presenceResource} key={presenceResource.key} />,
  [Resources.hunting_groups]: <Resource {...huntingGroupsResource} key={huntingGroupsResource.key} />,
  [Resources.extensions]: <Resource {...extensionResource} key={extensionResource.key} />,
  [Resources.user_numbers]: <Resource {...userNumbersResource} key={userNumbersResource.key} />,
  [Resources.sip_identities]: <Resource {...sipIdentitiesResource} key={sipIdentitiesResource.key} />,
  [Resources.mobility_configurations]: <Resource {...adminMobilityResource} key={adminMobilityResource.key} />,
  // ADMIN.SERVICES
  [Resources.soundfiles]: <Resource {...soundfilesResource} key={soundfilesResource.key} />,
  [Resources.tasks]: <Resource {...taskResource} key={taskResource.key} />,
  // ADMIN.ASSIGNED_RESOURCES
  [Resources.assigned_carriers]: <Resource {...assignedCarriersResource} key={assignedCarriersResource.key} />,
  [Resources.assigned_sip_profiles]: (
    <Resource {...assignedSipProfilesResource} key={assignedSipProfilesResource.key} />
  ),
  [Resources.assigned_lcrs]: <Resource {...assignedLcrsResource} key={assignedLcrsResource.key} />,
  [Resources.assigned_inbound_e164s]: (
    <Resource {...assignedInboundE164sResource} key={assignedInboundE164sResource.key} />
  ),
  // ADMIN.CALENDARS
  [Resources.calendars]: <Resource {...calendarsResource} key={calendarsResource.key} />,
  [Resources.calendar_events]: <Resource {...calendarEventsResource} key={calendarEventsResource.key} />,
  // ADMIN.ACCESS_CONTROL
  [Resources.service_classes]: <Resource {...serviceClassesResource} key={serviceClassesResource.key} />,
  [Resources.auth_providers]: <Resource {...authProvidersResource} key={authProvidersResource.key} />,
  [Resources.oauth_applications]: <Resource {...oauthApplicationsResource} key={oauthApplicationsResource.key} />,
  [Resources.roles]: <Resource {...rolesResource} key={rolesResource.key} />,
  [Resources.encryption_configurations]: (
    <Resource {...encryptionConfigurationsResource} key={encryptionConfigurationsResource.key} />
  ),
  [Resources.storage_configurations]: (
    <Resource {...storageConfigurationsResource} key={storageConfigurationsResource.key} />
  ),
  [Schedules]: <Resource {...schedulesResource} key={schedulesResource.key} />,

  // SYSTEM
  [Resources.scim_provisionings]: <Resource {...scimProvisioningsResource} key={scimProvisioningsResource.key} />,
  [Resources.ap_settings]: <Resource {...apSettingsResource} key={apSettingsResource.key} />,
  [Resources.ap_authentication]: <Resource {...apAuthenticationResource} key={apAuthenticationResource.key} />,
  [Resources.domains]: <Resource {...domainsResources} key={domainsResources.key} />,
  [Resources.lcrs]: <Resource {...lcrsResources} key={lcrsResources.key} />,
  [Resources.sip_profiles]: <Resource {...sipProfilesResources} key={sipProfilesResources.key} />,
  [Resources.ap_configurations]: <Resource {...apConfigurationsResource} key={apConfigurationsResource.key} />,
  [Resources.ap_groups]: <Resource {...apGroupsResource} key={apGroupsResource.key} />,
  [Resources.ap_templates]: <Resource {...apTemplatesResource} key={apTemplatesResource.key} />,
  [Resources.sip_devices]: <Resource {...sipDevicesResource} key={sipDevicesResource.key} />,
  [Resources.carriers]: <Resource {...carriersResources} key={carriersResources.key} />,
  [Resources.inbound_e164s]: <Resource {...inboundE164Resource} key={inboundE164Resource.key} />,
};
